// components/Layout/Layout.jsx - FIXED SUBSCRIPTION LOGIC
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import {
    Menu, X, Users, CreditCard, CheckSquare,
    Calendar, Bell, Settings, Home, User, LogOut, ChevronRight, HelpCircle,
    Building, ChevronDown, MessageSquare, Shield, AlertTriangle, Mail, Info, Clock, Plus,
    History, Receipt, FileText, Database, Zap, BarChart3, Landmark
} from 'lucide-react';
import styles from './Layout.module.css';
import { getUserBuildings } from '../../Fetch/fetchBuilding';
import { getBuildingSubscription } from '../../Fetch/fetchSubscription';
import { getAdminDashboard } from '../../Fetch/fetchBuildingPayment';
import { getProfilePictureAsBlob } from '../../Fetch/fetchUploadImage';
import { hasUnreadMessages, markMessagesAsRead } from '../../Fetch/fetchVaadsMessages';
import { isTrialBypassUser } from '../../utils/trialBypass';

// Notification Component
const NotificationBell = ({ hasNewMessages, onNotificationClick }) => (
    <div className={styles.notificationContainer}>
        <button
            className={styles.notificationButton}
            onClick={onNotificationClick}
            title={hasNewMessages ? 'יש לך הודעות חדשות' : 'אין הודעות חדשות'}
        >
            <Mail size={20} />
            {hasNewMessages && (
                <span className={styles.notificationDot}></span>
            )}
        </button>
    </div>
)

// 🔥 FIXED SUBSCRIPTION LOGIC: isExpired = true ONLY when BOTH trial AND payment expired
const calculateCorrectSubscriptionStatus = (subscriptionData) => {
    if (!subscriptionData) {
        return { isExpired: false, daysRemaining: 0, reason: 'No subscription data' };
    }

    const currentDate = new Date();
    let isExpired = false;
    let daysRemaining = 0;
    let expirationReason = '';

    // Check TrialEndDate
    let trialActive = false;
    let trialDaysRemaining = 0;
    if (subscriptionData.trialEndDate) {
        const trialEndDate = new Date(subscriptionData.trialEndDate);

        // Check if trialEndDate is DateTime.MinValue
        const isMinValue = trialEndDate.getFullYear() <= 1970 || isNaN(trialEndDate.getTime());

        if (!isMinValue) {
            const trialTimeDiff = trialEndDate.getTime() - currentDate.getTime();
            trialDaysRemaining = Math.ceil(trialTimeDiff / (1000 * 60 * 60 * 24));
            trialActive = currentDate <= trialEndDate; // Trial is active if not expired
        }
    }

    // Check NextPaymentDue
    let paymentActive = false;
    let paymentDaysRemaining = 0;
    if (subscriptionData.nextPaymentDue) {
        const nextPaymentDue = new Date(subscriptionData.nextPaymentDue);
        const paymentTimeDiff = nextPaymentDue.getTime() - currentDate.getTime();
        paymentDaysRemaining = Math.ceil(paymentTimeDiff / (1000 * 60 * 60 * 24));
        paymentActive = currentDate <= nextPaymentDue; // Payment is active if not expired
    }

    // 🔥 CORRECT LOGIC: Building is active if EITHER trial OR payment is active
    // Only expired when BOTH are expired/inactive
    if (trialActive || paymentActive) {
        // At least one is active - subscription is ACTIVE
        isExpired = false;
        // Use the days remaining from whichever is active
        if (trialActive && paymentActive) {
            // Both active - use the one with more days
            daysRemaining = Math.max(trialDaysRemaining, paymentDaysRemaining);
            expirationReason = 'Both trial and payment periods active';
        } else if (trialActive) {
            daysRemaining = trialDaysRemaining;
            expirationReason = 'Trial period active';
        } else {
            daysRemaining = paymentDaysRemaining;
            expirationReason = 'Payment period active';
        }
    } else {
        // Both expired - subscription is EXPIRED
        isExpired = true;
        daysRemaining = 0;
        expirationReason = 'Both trial and payment periods expired';
    }

    // For backward compatibility, add expired flags based on active flags
    const trialExpired = !trialActive;
    const paymentExpired = !paymentActive;

    return {
        isExpired,
        daysRemaining,
        trialEndDate: subscriptionData.trialEndDate,
        nextPaymentDue: subscriptionData.nextPaymentDue,
        reason: expirationReason,
        details: {
            trialActive,
            paymentActive,
            trialExpired,
            paymentExpired,
            trialDaysRemaining,
            paymentDaysRemaining
        }
    };
};

// Subscription Status Wrapper Component - Updated with MinValue handling
const SubscriptionStatusWrapper = ({ selectedBuildingId, buildingSubscriptions, availableBuildings, onGoToPayment }) => {
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const getSubscriptionStatus = () => {
            
            if (!selectedBuildingId || !buildingSubscriptions.length) {
                setSubscriptionStatus(null);
                return;
            }

            // Find subscription data for selected building
            const existingSubscription = buildingSubscriptions.find(s => {
                const matches = s.building?.buildingId === selectedBuildingId ||
                    s.building?.buildingID === selectedBuildingId;
                return matches;
            });


            // Check different possible structures of the subscription data
            let actualSubscription = null;
            
            // Based on the console log, the structure is existingSubscription.subscription (not nested deeper)
            if (existingSubscription?.subscription?.trialEndDate !== undefined || 
                existingSubscription?.subscription?.nextPaymentDue !== undefined) {
                actualSubscription = existingSubscription.subscription;
            } else if (existingSubscription?.subscription?.subscription) {
                actualSubscription = existingSubscription.subscription.subscription;
            } else if (existingSubscription?.trialEndDate || existingSubscription?.nextPaymentDue) {
                actualSubscription = existingSubscription;
            }

            if (!actualSubscription) {
                setSubscriptionStatus(null);
                return;
            }

            const currentDate = new Date();

            let alertInfo = null;
            let daysRemaining = 0;
            let alertDate = null;
            let alertType = '';

            let hasValidTrial = false;
            let trialIsMinValue = false;

            // Check trial date - if valid and within 3 days
            if (actualSubscription.trialEndDate) {
                const trialEndDate = new Date(actualSubscription.trialEndDate);
                trialIsMinValue = trialEndDate.getFullYear() <= 1970 || 
                                 isNaN(trialEndDate.getTime()) || 
                                 actualSubscription.trialEndDate.startsWith('0001-01-01');
                
                if (!trialIsMinValue && currentDate <= trialEndDate) {
                    hasValidTrial = true;
                    const trialDaysRemaining = Math.ceil((trialEndDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
                    if (trialDaysRemaining <= 3 && trialDaysRemaining > 0) {
                        daysRemaining = trialDaysRemaining;
                        alertDate = trialEndDate;
                        alertType = 'trial';
                        alertInfo = {
                            daysRemaining: trialDaysRemaining,
                            endDate: trialEndDate,
                            type: 'trial'
                        };
                    }
                } else if (trialIsMinValue) {
                }
            }

            // Check payment date - for both trial and non-trial buildings
            if (actualSubscription.nextPaymentDue) {
                const nextPaymentDue = new Date(actualSubscription.nextPaymentDue);

                const paymentIsMinValue = nextPaymentDue.getFullYear() <= 1970 || 
                                        isNaN(nextPaymentDue.getTime()) || 
                                        actualSubscription.nextPaymentDue.startsWith('0001-01-01');
                
                if (!paymentIsMinValue) {
                    const paymentDaysRemaining = Math.ceil((nextPaymentDue.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

                    // Show alert if:
                    // 1. Payment is overdue (negative days)
                    // 2. Payment is due within 3 days (0-3 days)
                    // For non-trial buildings (trial is MinValue), always show if within range
                    // For trial buildings, show payment alert only if no trial alert or payment is sooner
                    const isOverdueOrDueSoon = paymentDaysRemaining <= 3; // This includes negative numbers (overdue)
                    const shouldShowPaymentAlert = trialIsMinValue ? 
                        isOverdueOrDueSoon :
                        (isOverdueOrDueSoon && (!alertInfo || paymentDaysRemaining < daysRemaining));
                    
                    if (shouldShowPaymentAlert) {
                        daysRemaining = paymentDaysRemaining;
                        alertDate = nextPaymentDue;
                        alertType = trialIsMinValue ? 'payment-no-trial' : 'payment';
                        alertInfo = {
                            daysRemaining: paymentDaysRemaining,
                            endDate: nextPaymentDue,
                            type: trialIsMinValue ? 'payment-no-trial' : 'payment'
                        };
;
                    }
                } else {
                }
            }

            if (alertInfo) {
            } 

            setSubscriptionStatus(alertInfo);
        };

        getSubscriptionStatus();
    }, [selectedBuildingId, buildingSubscriptions]);

    if (!selectedBuildingId || isLoading || !subscriptionStatus) {
        return null;
    }

    return (
        <SubscriptionStatus
            subscriptionStatus={subscriptionStatus}
            onGoToPayment={() => onGoToPayment(selectedBuildingId)}
        />
    );
};

// Subscription Status Component - Updated for new alert format
const SubscriptionStatus = ({ subscriptionStatus, onGoToPayment }) => {

    if (!subscriptionStatus) {
        return null;
    }

    const { daysRemaining, endDate, type } = subscriptionStatus;

    const formatDate = (dateObj) => {
        try {
            if (!dateObj) return 'תאריך לא זמין';

            const date = dateObj instanceof Date ? dateObj : new Date(dateObj);

            if (isNaN(date.getTime())) {
                return 'תאריך לא זמין';
            }

            return date.toLocaleDateString('he-IL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return 'תאריך לא זמין';
        }
    };

    const getWarningLevel = () => {
        if (daysRemaining < 0) return 'overdue';  // Payment is overdue
        if (daysRemaining <= 1) return 'critical';
        if (daysRemaining <= 2) return 'warning';
        return 'info';
    };

    const warningLevel = getWarningLevel();
    const formattedDate = formatDate(endDate);
    
    // Different messages for trial vs payment
    const getAlertMessage = () => {
        if (daysRemaining < 0) {
            // Overdue payment
            return {
                label: 'התשלום באיחור!',
                title: `התשלום היה אמור להיות ב-${formattedDate}, לחץ לתשלום דחוף`
            };
        } else if (type === 'trial') {
            return {
                label: 'תקופת הניסיון מסתיימת',
                title: `תקופת הניסיון מסתיימת ב-${formattedDate}, לחץ לתשלום`
            };
        } else {
            return {
                label: 'תשלום נדרש עד',
                title: `התשלום נדרש עד ${formattedDate}, לחץ לתשלום`
            };
        }
    };

    const alertMessage = getAlertMessage();

    const handleClick = () => {
        if (onGoToPayment) {
            onGoToPayment();
        }
    };

    return (
        <button
            className={`${styles.subscriptionStatus} ${styles[warningLevel]} ${styles.clickable}`}
            dir="rtl"
            onClick={handleClick}
            title={alertMessage.title}
            aria-label={alertMessage.title}
        >
            <div className={styles.statusIcon}>
                {warningLevel === 'critical' ? (
                    <AlertTriangle size={16} />
                ) : (
                    <Clock size={16} />
                )}
            </div>

            <div className={styles.statusContent}>
                <div className={styles.statusLabel}>{alertMessage.label}</div>
                <div className={styles.statusDate}>{formattedDate}</div>
                {daysRemaining < 0 ? (
                    <div className={styles.urgentNote}>באיחור של {Math.abs(daysRemaining)} ימים</div>
                ) : daysRemaining === 0 ? (
                    <div className={styles.urgentNote}>התשלום היום!</div>
                ) : daysRemaining === 1 ? (
                    <div className={styles.urgentNote}>נותר יום אחד</div>
                ) : daysRemaining <= 3 && (
                    <div className={styles.urgentNote}>נותרו {daysRemaining} ימים</div>
                )}
            </div>

            {daysRemaining <= 2 && (
                <div className={styles.quickPayIcon}>
                    <Calendar size={14} />
                </div>
            )}
        </button>
    );
};

// Helper function to get consistent building address display
const getBuildingDisplayAddress = (building) => {
    const addressParts = [];
    if (building.address) addressParts.push(building.address);
    if (building.addressNumber) addressParts.push(building.addressNumber);
    if (building.city) addressParts.push(building.city);

    return addressParts.length > 0 ? addressParts.join(' ') : (building.buildingName || 'כתובת לא זמינה');
};

// 🔥 FIXED BuildingDropdown Component with Correct Unpaid Building Detection
const BuildingDropdown = ({ buildings, selectedBuildingId, onBuildingChange, isLoading, hasNoAccess, subscriptions = [], t, bypassSubscriptionCheck = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showPaymentAlert, setShowPaymentAlert] = useState(null);
    const navigate = useNavigate();

    const selectedBuilding = buildings.find(b => b.buildingID === selectedBuildingId);

    // Helper function to store building address info for payment
    const storeBuildingPaymentInfo = (building) => {
        const buildingNeedPay = {
            address: building.address || '',
            addressNumber: building.addressNumber || '',
            city: building.city || '',
            displayAddress: getBuildingDisplayAddress(building)
        };

        localStorage.setItem('buildingNeedPay', JSON.stringify(buildingNeedPay));
    };


    const handleBuildingSelect = (building) => {
        const isExpired = isBuildingExpired(building, subscriptions, bypassSubscriptionCheck);
        
        // Allow clicking on paid buildings to select them normally
        if (!isExpired) {
            onBuildingChange(building);
            setIsOpen(false);
            localStorage.removeItem('buildingNeedPay');
        } else {
            
            // Store building address info for payment form
            storeBuildingPaymentInfo(building);

            // Navigate to payment for unpaid building
            navigate('/subscription-payment', {
                state: {
                    buildingId: building.buildingID,
                    reason: 'building_access',
                    returnTo: '/app/dashboard',
                    buildingName: getBuildingDisplayAddress(building)
                }
            });
        }
    };

    // Handle payment button click (the "לא שולם" button)
    const handlePaymentClick = (building, e) => {
        e.stopPropagation();

        // Store building address info for payment form
        storeBuildingPaymentInfo(building);

        navigate('/subscription-payment', {
            state: {
                buildingId: building.buildingID,
                reason: 'building_expired',
                returnTo: '/app/dashboard',
                buildingName: getBuildingDisplayAddress(building)
            }
        });
    };

    // Show payment alert
    const showPaymentAlertCard = (building, e) => {
        e.stopPropagation();
        setShowPaymentAlert(building.buildingID);
    };

    // Hide payment alert
    const hidePaymentAlert = () => {
        setShowPaymentAlert(null);
    };

    // Close alert when clicking outside
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            hidePaymentAlert();
        }
    };

    // Show no access message
    if (hasNoAccess) {
        return (
            <div className={styles.buildingDropdown}>
                <div className={styles.buildingDropdownButton} style={{ cursor: 'not-allowed', opacity: 0.6 }}>
                    <AlertTriangle size={16} />
                    <span>{t('noPermissions')}</span>
                </div>
            </div>
        );
    }

    // Check if there are mixed payment statuses using the CORRECT function
    const unpaidBuildings = buildings.filter(building => isBuildingExpired(building, subscriptions, bypassSubscriptionCheck));
    const hasUnpaidBuildings = unpaidBuildings.length > 0;

    const buttonClass = hasUnpaidBuildings ?
        `${styles.buildingDropdownButton} ${styles.mixedPaymentStatus}` :
        styles.buildingDropdownButton;

    return (
        <div className={styles.buildingDropdown}>
            <button
                className={buttonClass}
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading || buildings.length <= 1}
                title={hasUnpaidBuildings ? "יש בניינים שדורשים תשלום" : "בחר בניין"}
            >
                <Building size={16} />
                <span>
                    {selectedBuilding
                        ? getBuildingDisplayAddress(selectedBuilding)
                        : isLoading
                            ? t('loadingBuildings')
                            : buildings.length === 0
                                ? t('noBuildingsAvailable')
                                : t('selectBuilding')
                    }
                </span>
                {buildings.length > 1 && <ChevronDown size={16} className={isOpen ? styles.rotated : ''} />}
            </button>

            {isOpen && buildings.length > 1 && (
                <div className={styles.buildingDropdownMenu}>
                    {buildings.map((building) => {
                        const isExpired = isBuildingExpired(building, subscriptions, bypassSubscriptionCheck);
                        const isSelected = building.buildingID === selectedBuildingId;
                        const displayAddress = getBuildingDisplayAddress(building);


                        return (
                            <div key={building.buildingID} className={styles.buildingDropdownItemWrapper}>
                                <div
                                    className={`${styles.buildingDropdownItem} ${isSelected ? styles.selected : ''} ${isExpired ? styles.unpaidBuilding : ''}`}
                                    onClick={() => handleBuildingSelect(building)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleBuildingSelect(building);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    title={isExpired ? 'לחץ לתשלום עבור בניין זה' : 'לחץ לבחירת בניין'}
                                >
                                    <div className={styles.buildingInfo}>
                                        <div className={styles.buildingAddressRow}>
                                            <div className={styles.buildingAddress}>
                                                {displayAddress}
                                            </div>
                                            <div className={styles.buildingLabelsContainer}>
                                                {/* 🔥 FIXED: This is the key part - showing the unpaid label and button */}
                                                {isExpired && (
                                                    <>
                                                        <span className={styles.unpaidLabel}>לא שולם</span>
                                                        <button
                                                            className={styles.paymentAlertButton}
                                                            onClick={(e) => {
                                                                handlePaymentClick(building, e);
                                                            }}
                                                            title="לחץ לתשלום"
                                                        >
                                                            <CreditCard size={12} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.buildingDetails}>
                                            {building.city} • {building.numApartments} דירות • {building.numFloors} קומות
                                            {building.userRoles && (
                                                <div className={styles.userRoles}>
                                                    {building.userRoles.isManager === "כן" && <span className={styles.roleTag}>מנהל</span>}
                                                    {building.userRoles.isVaad === "כן" && <span className={styles.roleTag}>חבר ועד</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Payment Alert Card Modal */}
            {showPaymentAlert && (
                <div className={styles.paymentAlertOverlay} onClick={handleOverlayClick}>
                    <div className={styles.paymentAlertCard}>
                        <div className={styles.alertIcon}>
                            <AlertTriangle size={24} />
                        </div>

                        <div className={styles.alertContent}>
                            <h3 className={styles.alertTitle}>תקופת הניסיון הסתיימה</h3>
                            <p className={styles.alertMessage}>
                                עבור הבניין: {getBuildingDisplayAddress(buildings.find(b => b.buildingID === showPaymentAlert))}
                            </p>
                            <p className={styles.alertSubMessage}>
                                יש לבחור תוכנית תשלום כדי להמשיך להשתמש במערכת
                            </p>

                            <div className={styles.alertActions}>
                                <button
                                    onClick={(e) => handlePaymentClick(buildings.find(b => b.buildingID === showPaymentAlert), e)}
                                    className={styles.paymentButton}
                                >
                                    <CreditCard size={16} />
                                    בחר תוכנית תשלום
                                </button>

                                <button
                                    onClick={hidePaymentAlert}
                                    className={styles.dismissButton}
                                >
                                    סגור
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={hidePaymentAlert}
                            className={styles.closeButton}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// 🔥 FIXED function to check if building subscription is expired - show unpaid ONLY when really expired
const isBuildingExpired = (building, subscriptions, bypass = false) => {
    // Whitelisted emails are never treated as expired
    if (bypass) {
        return false;
    }

    // If no subscriptions data loaded yet, assume not expired to avoid flickering
    if (!subscriptions || subscriptions.length === 0) {
        return false;
    }

    // Try to find subscription data for this building
    const targetBuildingId = Number(building.buildingID);
    const subscriptionData = subscriptions.find(s => {
        const possibleMatches = [
            Number(s.building?.buildingId),
            Number(s.building?.buildingID),
            Number(s.buildingId),
            Number(s.buildingID)
        ].filter(id => !isNaN(id));
        
        const buildingMatches = possibleMatches.some(id => id === targetBuildingId);
        
        // Debug: uncomment to see subscription matching
        // if (buildingMatches) {
        // }
        return buildingMatches;
    });

    if (!subscriptionData) {
        // If no subscription data found, assume expired for safety
        return true;
    }

    let isExpired = false;

    // Check subscription status using CORRECT logic
    if (subscriptionData.subscription) {
        // Always calculate on frontend to ensure correct logic
        // (Don't trust backend pre-calculated values)
        {
            // Calculate using CORRECT logic (ignoring backend calculation)
            const currentDate = new Date();
            
            // The actual subscription data is nested deeper
            const actualSubscription = subscriptionData.subscription.subscription;
            
            // Check trial date - if trial is still active, NOT expired
            let trialActive = false;
            let hasValidTrial = false;
            if (actualSubscription && actualSubscription.trialEndDate) {
                const trialEndDate = new Date(actualSubscription.trialEndDate);
                const isMinValue = trialEndDate.getFullYear() <= 1970 || isNaN(trialEndDate.getTime()) || actualSubscription.trialEndDate.startsWith('0001-01-01');
                if (!isMinValue) {
                    hasValidTrial = true;
                    trialActive = currentDate <= trialEndDate; // Trial is active if current date is before or equal to trial end
                }
            }

            // Check payment date - if payment is still valid, NOT expired
            let paymentActive = false;
            let hasValidPayment = false;
            if (actualSubscription && actualSubscription.nextPaymentDue) {
                const nextPaymentDue = new Date(actualSubscription.nextPaymentDue);
                const isMinValue = nextPaymentDue.getFullYear() <= 1970 || isNaN(nextPaymentDue.getTime()) || actualSubscription.nextPaymentDue.startsWith('0001-01-01');
                if (!isMinValue) {
                    hasValidPayment = true;
                    paymentActive = currentDate <= nextPaymentDue; // Payment is active if current date is before or equal to next payment due
                }
            }

            // 🔥 CORRECT LOGIC with MinValue handling:
            if (!hasValidTrial && !hasValidPayment) {
                // Both dates are MinValue/invalid - assume building is ACTIVE (no subscription constraints)
                isExpired = false;
            } else if (trialActive || paymentActive) {
                // At least one valid date is still active - building is ACTIVE
                isExpired = false;
            } else {
                // All valid dates have expired - building is EXPIRED
                isExpired = true;
            }
            
        }
    } else if (subscriptionData.hasOwnProperty('isExpired')) {
        // Direct subscription data (fallback)
        isExpired = Boolean(subscriptionData.isExpired);
    } else {
        // Default to expired if we can't determine status
        isExpired = true;
    }

    return isExpired;
};

const Layout = ({ userData, onLogout }) => {
    const { isDarkMode } = useDarkMode();
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    // Subscription-related state
    const [buildingSubscriptions, setBuildingSubscriptions] = useState([]);
    const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);

    // Building-related state
    const [availableBuildings, setAvailableBuildings] = useState([]);
    const [selectedBuildingId, setSelectedBuildingId] = useState(null);
    const [isBuildingsLoading, setIsBuildingsLoading] = useState(false);
    const [hasNoAccess, setHasNoAccess] = useState(false);

    // Cross-building notification state
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    
    // Sub-menu state
    const [expandedMenus, setExpandedMenus] = useState(new Set());

    // Settings dropdown state
    const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
    const [selectedSettingsTab, setSelectedSettingsTab] = useState(null);

    // Keep admin sub-menu expanded when navigating between admin pages
    useEffect(() => {
        if (location.pathname.startsWith('/app/admin-dashboard')) {
            // Always ensure sub-menu is expanded when on admin dashboard pages
            setExpandedMenus(prev => {
                if (!prev.has('admin-dashboard')) {
                    return new Set([...prev, 'admin-dashboard']);
                }
                return prev; // Already expanded, keep it
            });
        }
    }, [location.pathname]);

    // Profile picture state with simplified caching
    const [userProfilePicture, setUserProfilePicture] = useState(null);
    const [isLoadingProfilePic, setIsLoadingProfilePic] = useState(false);
    const [profilePictureLoaded, setProfilePictureLoaded] = useState(false);

    // Countdown state for noAccessWarning
    const [countdownValue, setCountdownValue] = useState(null);


    // Load building subscription data using AdminDashboard API for consistency
    const loadBuildingSubscriptions = async () => {
        try {
            setIsLoadingSubscriptions(true);

            // Use the AdminDashboard API which has the correct subscription data
            const adminDashboard = await getAdminDashboard();
            
            if (adminDashboard && adminDashboard.buildingPaymentStatuses) {
                // Convert AdminDashboard format to the format expected by Layout
                const subscriptionResults = adminDashboard.buildingPaymentStatuses.map(buildingStatus => {
                    // Create subscription object from AdminDashboard data
                    const subscriptionData = {
                        trialStartDate: buildingStatus.trialStartDate,
                        trialEndDate: buildingStatus.trialEndDate,
                        nextPaymentDue: buildingStatus.nextPaymentDue,
                        subscriptionStatus: buildingStatus.subscriptionStatus || "Unknown"
                    };

                    // Calculate correct status using our existing logic
                    const correctedStatus = calculateCorrectSubscriptionStatus(subscriptionData);

                    return {
                        building: { 
                            buildingId: buildingStatus.buildingId,
                            buildingID: buildingStatus.buildingId,
                            address: buildingStatus.address,
                            city: buildingStatus.city,
                            addressNumber: buildingStatus.addressNumber.toString()
                        },
                        subscription: {
                            subscription: subscriptionData, // Nested structure expected by isBuildingExpired
                            isExpired: correctedStatus.isExpired,
                            correctedStatus: correctedStatus
                        }
                    };
                });

                setBuildingSubscriptions(subscriptionResults);
                
                // Store in localStorage for future use
                localStorage.setItem('buildingSubscriptions', JSON.stringify(subscriptionResults));
            } else {
                setBuildingSubscriptions([]);
            }
        } catch (error) {
            console.error('Error loading building subscriptions:', error);
            setBuildingSubscriptions([]);
        } finally {
            setIsLoadingSubscriptions(false);
        }
    };

    // Handle responsive behavior
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 1024;
            setIsMobile(mobile);
            setSidebarOpen(!mobile);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load subscriptions when available buildings change
    useEffect(() => {
        if (availableBuildings.length > 0) {
            loadBuildingSubscriptions();
        }
    }, [availableBuildings]);

    // Countdown timer for noAccessWarning
    useEffect(() => {
        if (hasNoAccess) {
            // Start countdown from 5
            setCountdownValue(5);
            
            const countdownInterval = setInterval(() => {
                setCountdownValue(prev => {
                    if (prev === null || prev <= 1) {
                        // Time's up - trigger logout
                        clearInterval(countdownInterval);
                        handleLogout();
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000); // Update every second

            return () => clearInterval(countdownInterval);
        } else {
            setCountdownValue(null);
        }
    }, [hasNoAccess]);


    // Load current user data
    useEffect(() => {
        const loadUserData = () => {
            try {
                const userData = JSON.parse(localStorage.getItem('user'));
                if (userData) {
                    setCurrentUserId(userData.userId);
                }
            } catch (error) {
            }
        };

        loadUserData();
    }, []);

    // Check for unread messages across ALL buildings when user changes
    useEffect(() => {
        if (currentUserId) {
            checkUnreadMessages();

            // Set up more frequent polling for better real-time experience (every 5 seconds)
            const interval = setInterval(checkUnreadMessages, 5000);
            return () => clearInterval(interval);
        }
    }, [currentUserId]);

    // Function to check unread messages from ALL buildings (not building-specific)
    const checkUnreadMessages = async () => {
        if (!currentUserId) return;

        try {
            // Use the new boolean API
            const result = await hasUnreadMessages(currentUserId);
            setHasNewMessages(result.hasUnread || false);
        } catch (error) {
            // Gracefully handle errors - don't show notification if API fails
            setHasNewMessages(false);
        }
    };

    // Handle notification click - mark messages as read and navigate
    const handleNotificationClick = async () => {
        if (!currentUserId) return;

        try {
            // DON'T mark messages as read immediately - let VaadsMessages handle it
            // so the user can see the highlighting first

            // Dispatch event to VaadsMessages to preserve notification state
            window.dispatchEvent(new CustomEvent('notificationNavigationToVaads', {
                detail: {
                    userId: currentUserId,
                    preserveNotificationState: true,
                    fromNotificationClick: true
                }
            }));

            // Set flag in sessionStorage so VaadsMessages knows this was from notification
            sessionStorage.setItem('isFromNotificationNavigation', 'true');

            // Navigate to VaadsMessages page
            navigate('/app/vaads-messages');
            if (isMobile) {
                setSidebarOpen(false);
            }

            // Update local state immediately for better UX
            setHasNewMessages(false);

        } catch (error) {
            // Still navigate even if there's an error
            navigate('/app/vaads-messages');
            if (isMobile) {
                setSidebarOpen(false);
            }
        }
    };

    // Listen for real-time message events across buildings
    useEffect(() => {
        const handleNewMessage = () => {
            // When a new message is created anywhere, refresh notifications immediately
            if (currentUserId) {
                checkUnreadMessages();
            }
        };

        const handleMessageAnswered = (event) => {
            // When someone answers a message, refresh notifications for other users
            const { answerUserId } = event.detail;

            if (currentUserId && answerUserId !== currentUserId) {
                // Only refresh if this is not the user who sent the answer
                checkUnreadMessages();
            }
        };

        const handleMessageRead = () => {
            // When user reads messages, update notification state immediately
            setHasNewMessages(false);
        };

        const handleMessageAnswersUpdated = () => {
            // When answers are updated in real-time, check for notifications
            if (currentUserId) {
                checkUnreadMessages();
            }
        };

        // Listen for all global message events
        window.addEventListener('vaadsMessageCreated', handleNewMessage);
        window.addEventListener('vaadsMessageAnswered', handleMessageAnswered);
        window.addEventListener('vaadsMessagesRead', handleMessageRead);
        window.addEventListener('messageAnswersUpdated', handleMessageAnswersUpdated);

        return () => {
            window.removeEventListener('vaadsMessageCreated', handleNewMessage);
            window.removeEventListener('vaadsMessageAnswered', handleMessageAnswered);
            window.removeEventListener('vaadsMessagesRead', handleMessageRead);
            window.removeEventListener('messageAnswersUpdated', handleMessageAnswersUpdated);
        };
    }, [currentUserId]);

    // Load user profile picture with simple effective caching
    const loadUserProfilePicture = async (forceReload = false) => {
        // Check if already loaded and cached
        if (profilePictureLoaded && !forceReload) {
            return;
        }

        // If already loading, don't start another load
        if (isLoadingProfilePic && !forceReload) {
            return;
        }

        try {
            // First check sessionStorage cache
            if (!forceReload) {
                const cachedPicture = sessionStorage.getItem('userProfilePicture');
                const cachedLoaded = sessionStorage.getItem('profilePictureLoaded');
                
                if (cachedLoaded === 'true') {
                    setUserProfilePicture(cachedPicture || null);
                    setProfilePictureLoaded(true);
                    return;
                }
            }

            // Use userData prop if available, otherwise try localStorage
            let user = userData;
            if (!user) {
                const storedUser = localStorage.getItem('user');
                if (!storedUser) {
                    setUserProfilePicture(null);
                    setProfilePictureLoaded(true);
                    sessionStorage.setItem('profilePictureLoaded', 'true');
                    sessionStorage.removeItem('userProfilePicture');
                    return;
                }
                user = JSON.parse(storedUser);
            }

            setIsLoadingProfilePic(true);

            let profilePictureUrl = null;

            if (user.profileImagePath) {
                try {
                    const blobUrl = await getProfilePictureAsBlob(user.profileImagePath);
                    profilePictureUrl = blobUrl;
                } catch (error) {
                    if (user.profilePicture) {
                        profilePictureUrl = user.profilePicture;
                    } else {
                        profilePictureUrl = null;
                    }
                }
            } else if (user.profilePicture) {
                profilePictureUrl = user.profilePicture;
            } else {
                profilePictureUrl = null;
            }

            // Update state
            setUserProfilePicture(profilePictureUrl);
            setProfilePictureLoaded(true);

            // Cache for next navigation
            sessionStorage.setItem('profilePictureLoaded', 'true');
            if (profilePictureUrl) {
                sessionStorage.setItem('userProfilePicture', profilePictureUrl);
            } else {
                sessionStorage.removeItem('userProfilePicture');
            }

        } catch (error) {
            setUserProfilePicture(null);
            setProfilePictureLoaded(true);
            sessionStorage.setItem('profilePictureLoaded', 'true');
            sessionStorage.removeItem('userProfilePicture');
        } finally {
            setIsLoadingProfilePic(false);
        }
    };

    // Load profile picture on mount and when userData changes
    useEffect(() => {
        loadUserProfilePicture();
    }, [userData]); // Add userData as dependency

    // Listen for profile picture updates
    useEffect(() => {
        const handleProfilePictureUpdate = (event) => {
            if (event.detail?.profilePictureUpdated) {
                // Clear sessionStorage cache
                sessionStorage.removeItem('userProfilePicture');
                sessionStorage.removeItem('profilePictureLoaded');
                // Reset state
                setProfilePictureLoaded(false);
                setUserProfilePicture(null);
                // Force reload
                loadUserProfilePicture(true);
            }
        };

        window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);

        return () => {
            window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
        };
    }, []);

    // Listen for building added events to refresh the building list
    useEffect(() => {
        const handleBuildingAdded = (event) => {
            // Refresh the building list when a new building is added
            loadUserBuildings();
        };

        window.addEventListener('buildingAdded', handleBuildingAdded);

        return () => {
            window.removeEventListener('buildingAdded', handleBuildingAdded);
        };
    }, []);

    // Cleanup blob URLs when component unmounts or profile picture changes
    useEffect(() => {
        return () => {
            if (userProfilePicture && userProfilePicture.startsWith('blob:')) {
                URL.revokeObjectURL(userProfilePicture);
            }
        };
    }, [userProfilePicture]);

    // Function to load user buildings
    const loadUserBuildings = async () => {
        setIsBuildingsLoading(true);
        setHasNoAccess(false);

        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                const userId = user.userId;

                const buildings = await getUserBuildings(userId);


                if (!buildings || buildings.length === 0) {
                    setHasNoAccess(true);
                    setAvailableBuildings([]);
                    setSelectedBuildingId(null);

                    const updatedUser = {
                        ...user,
                        buildingId: null,
                        buildingIds: [],
                        selectedBuildingInfo: null
                    };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    return;
                }

                setAvailableBuildings(buildings);

                const currentBuildingId = user.buildingId;

                let selectedBuilding = null;

                if (currentBuildingId && buildings.find(b => b.buildingID === currentBuildingId)) {
                    selectedBuilding = buildings.find(b => b.buildingID === currentBuildingId);
                    setSelectedBuildingId(currentBuildingId);
                } else if (buildings.length > 0) {
                    selectedBuilding = buildings[0];
                    setSelectedBuildingId(buildings[0].buildingID);
                }

                if (selectedBuilding) {
                    updateLocalStorageBuilding(selectedBuilding, buildings);
                }
            }
        } catch (error) {
            setAvailableBuildings([]);
            setHasNoAccess(true);
        } finally {
            setIsBuildingsLoading(false);
        }
    };

    // Function to update localStorage when building changes
    const updateLocalStorageBuilding = (selectedBuilding, providedAvailableBuildings = null) => {
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);

                // Use consistent address display logic
                const displayAddress = getBuildingDisplayAddress(selectedBuilding);

                // Use provided buildings or fall back to state value
                const buildingsToUse = providedAvailableBuildings || availableBuildings;

                const updatedUser = {
                    ...user,
                    buildingId: selectedBuilding.buildingID,
                    // Safe check: only map if buildingsToUse is an array
                    buildingIds: Array.isArray(buildingsToUse) ? buildingsToUse.map(b => b.buildingID) : [],
                    selectedBuildingInfo: {
                        address: selectedBuilding.address,
                        city: selectedBuilding.city,
                        addressNumber: selectedBuilding.addressNumber,
                        numApartments: selectedBuilding.numApartments,
                        numFloors: selectedBuilding.numFloors,
                        // Add consistent display address
                        displayAddress: displayAddress
                    },
                    userRoles: selectedBuilding.userRoles || null
                };

                localStorage.setItem('user', JSON.stringify(updatedUser));

                window.dispatchEvent(new CustomEvent('buildingChanged'));
            }
        } catch (error) {
        }
    };


    // Handle building selection change with CORRECTED logic
    const handleBuildingChange = (selectedBuilding) => {

        // Use the same logic as isBuildingExpired function
        const isExpired = isBuildingExpired(selectedBuilding, buildingSubscriptions, isTrialBypassUser(userData?.email));

        if (isExpired) {

            // Store building address info for payment form
            const buildingNeedPay = {
                address: selectedBuilding.address || '',
                addressNumber: selectedBuilding.addressNumber || '',
                city: selectedBuilding.city || '',
                displayAddress: getBuildingDisplayAddress(selectedBuilding)
            };
            localStorage.setItem('buildingNeedPay', JSON.stringify(buildingNeedPay));

            // Show payment modal or navigate to payment
            navigate('/subscription-payment', {
                state: {
                    buildingId: selectedBuilding.buildingID,
                    reason: 'building_expired',
                    returnTo: '/app/dashboard',
                    buildingName: getBuildingDisplayAddress(selectedBuilding)
                }
            });
            return;
        }


        // Clear any existing buildingNeedPay data since this building is paid
        localStorage.removeItem('buildingNeedPay');

        // Update selected building ID first
        setSelectedBuildingId(selectedBuilding.buildingID);
        updateLocalStorageBuilding(selectedBuilding, availableBuildings);

    };

    // Load buildings on mount
    useEffect(() => {
        loadUserBuildings();
    }, []);

    // Helper function to check if user is admin
    const isAdmin = () => {
        return userData?.email === 'motisegev1@gmail.com';
    };

    // Settings menu items for admin
    const getSettingsMenuItems = () => {
        if (!isAdmin()) return [];

        return [
            {
                id: 'admin-dashboard-overview',
                label: 'דשבורד כללי',
                icon: <span style={{fontSize: '18px'}}>₪</span>,
                path: '/app/admin-dashboard?tab=dashboard',
                color: '#3b82f6'
            },
            {
                id: 'admin-buildings',
                label: 'ניהול בניינים',
                icon: <Building size={18} />,
                path: '/app/admin-dashboard?tab=buildings',
                color: '#10b981'
            },
            {
                id: 'admin-suppliers',
                label: 'ניהול ספקים',
                icon: <Users size={18} />,
                path: '/app/admin-dashboard?tab=suppliers',
                color: '#f59e0b'
            },
            {
                id: 'admin-users',
                label: 'ניהול משתמשים',
                icon: <Users size={18} />,
                path: '/app/admin-dashboard?tab=users',
                color: '#8b5cf6'
            },
            {
                id: 'admin-payment-history',
                label: 'היסטוריית תשלומים',
                icon: <History size={18} />,
                path: '/app/admin-dashboard?tab=payment-history',
                color: '#f97316'
            },
            {
                id: 'admin-billing',
                label: 'לוח בקרה חיוב',
                icon: <CreditCard size={18} />,
                path: '/app/admin-dashboard?tab=billing-dashboard',
                color: '#06b6d4'
            },
            {
                id: 'admin-payment-form',
                label: 'טופס תשלום',
                icon: <Receipt size={18} />,
                path: '/app/admin-dashboard?tab=payment-form',
                color: '#84cc16'
            },
            {
                id: 'admin-subscription-history',
                label: 'היסטוריית מנויים',
                icon: <FileText size={18} />,
                path: '/app/admin-dashboard?tab=subscription-history',
                color: '#ec4899'
            },
            {
                id: 'admin-backups',
                label: 'גיבויים',
                icon: <Database size={18} />,
                path: '/app/admin-dashboard?tab=backups',
                color: '#6366f1'
            }
        ];
    };

    const settingsMenuItems = getSettingsMenuItems();

    // Menu items for authenticated users
    const getFilteredMenuItems = () => {
        const baseMenuItems = [
            {
                id: 'dashboard',
                label: t('dashboard'),
                icon: <Home size={20} />,
                path: '/app/dashboard',
                color: '#3b82f6',
                requiresAccess: false
            },
            {
                id: 'reports',
                label: 'דוחות מתקדמים',
                icon: <BarChart3 size={20} />,
                path: '/app/reports',
                color: '#8b5cf6',
                requiresAccess: false
            },
            {
                id: 'users',
                label: t('users'),
                icon: <Users size={20} />,
                path: '/app/users',
                color: '#10b981',
                requiresAccess: true
            },
            {
                id: 'managers',
                label: 'משתמשים',
                icon: <Shield size={20} />,
                path: '/app/managers',
                color: '#ec4899',
                requiresAccess: true
            },
            {
                id: 'payments',
                label: t('payments'),
                icon: <span style={{fontSize: '28px'}}>₪</span>,
                path: '/app/payments',
                color: '#f59e0b',
                requiresAccess: true
            },
            {
                id: 'management-company',
                label: t('managementCompany'),
                icon: <Shield size={20} />,
                path: '/app/management-company',
                color: '#7c3aed',
                requiresAccess: true
            },
            {
                id: 'messages',
                label: t('messages'),
                icon: <MessageSquare size={20} />,
                path: '/app/messages',
                color: '#8b5cf6',
                requiresAccess: true
            },
            {
                id: 'expenses',
                label: t('expenses'),
                icon: <CreditCard size={20} />,
                path: '/app/expenses',
                color: '#ef4444',
                requiresAccess: true
            },
            {
                id: 'electric-vehicle',
                label: 'רכבים חשמליים',
                icon: <Zap size={20} />,
                path: '/app/electric-vehicle',
                color: '#eab308',
                requiresAccess: true
            },
            {
                id: 'bank-status',
                label: 'סטטוס בנק',
                icon: <Landmark size={20} />,
                path: '/app/bank-status',
                color: '#0891b2',
                requiresAccess: true
            },
            {
                id: 'tasks',
                label: t('tasks'),
                icon: <CheckSquare size={20} />,
                path: '/app/tasks',
                color: '#8b5cf6',
                requiresAccess: true
            },
            {
                id: 'events',
                label: t('events'),
                icon: <Calendar size={20} />,
                path: '/app/events',
                color: '#06b6d4',
                requiresAccess: true
            },
            {
                id: 'personal-information',
                label: t('personalInfo'),
                icon: <Info size={20} />,
                path: '/app/personal-information',
                color: '#06b6d4',
                requiresAccess: false
            },
            {
                id: 'notifications',
                label: t('notifications'),
                icon: <Bell size={20} />,
                path: '/app/notifications',
                color: '#f97316',
                requiresAccess: true
            },
            {
                id: 'issue',
                label: t('openTicket'),
                icon: <HelpCircle size={20} />,
                path: '/app/issue',
                color: '#e11d48',
                requiresAccess: false
            },
            {
                id: 'settings',
                label: t('settings'),
                icon: <Settings size={20} />,
                path: '/app/settings',
                color: '#64748b',
                requiresAccess: false
            },
        ];

        if (hasNoAccess) {
            return baseMenuItems.filter(item => !item.requiresAccess);
        }

        return baseMenuItems;
    };

    const menuItems = getFilteredMenuItems();

    const handleMenuItemClick = (item) => {
        if (item.hasSubMenu) {
            // Simple toggle behavior for sub-menu
            const newExpanded = new Set(expandedMenus);
            if (newExpanded.has(item.id)) {
                newExpanded.delete(item.id);
            } else {
                newExpanded.add(item.id);
            }
            setExpandedMenus(newExpanded);
        } else {
            // Navigate to path
            navigate(item.path || item);
            if (isMobile) {
                setSidebarOpen(false);
            }
        }
    };

    const handleSubMenuItemClick = (path, e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling that might close the menu
        
        // Ensure admin sub-menu stays expanded before navigation
        setExpandedMenus(prev => new Set([...prev, 'admin-dashboard']));
        
        // Navigate after ensuring menu state is preserved
        setTimeout(() => {
            navigate(path);
        }, 0);
        
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const handleUserProfileClick = () => {
        navigate('/app/profile');
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const handleSettingsMenuClick = (menuItem) => {
        setSelectedSettingsTab(menuItem.id);
        setIsSettingsDropdownOpen(false);
        navigate(menuItem.path);
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const handleLogout = () => {
        try {

            // Clear specific localStorage items
            localStorage.removeItem('buildingNeedPay');
            localStorage.removeItem('buildingSubscriptions');

            // Clear profile picture cache
            sessionStorage.removeItem('userProfilePicture');
            sessionStorage.removeItem('profilePictureLoaded');

            // Call the parent logout function
            onLogout();

            // Don't call navigate here - let the parent App.js handle navigation
            // This prevents race conditions between state updates and navigation

        } catch (error) {
            // Only navigate here if there's an error
            navigate('/');
        }
    };

    const getCurrentPageTitle = () => {
        const currentItem = menuItems.find(item => item.path === location.pathname);
        return currentItem ? currentItem.label : 'דשבורד';
    };

    return (
        <div className={`${styles.appContainer} ${isDarkMode ? styles.darkMode : ''}`}>
            {/* No Access Warning Overlay */}
            {hasNoAccess && (
                <>
                    <div className={styles.noAccessOverlay} />
                    <div className={styles.noAccessWarning}>
                        <AlertTriangle size={32} />
                        <span>{t('noPermissions')}</span>
                        {countdownValue !== null && (
                            <span className={styles.countdownTimer}>
                                יציאה בעוד: {countdownValue}
                            </span>
                        )}
                    </div>
                </>
            )}

            {/* Sidebar Overlay for Mobile */}
            {isMobile && sidebarOpen && (
                <div
                    className={styles.sidebarOverlay}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
                <div className={styles.sidebarHeader}>
                    <div className={styles.logoContainer}>
                        <h1 className={styles.logoText}>Bildi App</h1>
                    </div>
                    {isMobile && (
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className={styles.closeSidebarButton}
                        >
                            <X size={24} />
                        </button>
                    )}
                </div>

                {/* Mobile Header Center Content - Show in sidebar when mobile */}
                {isMobile && (
                    <div className={styles.mobileHeaderCenter}>
                        <button
                            className={styles.addBuildingButton}
                            onClick={() => navigate('/app/add-building')}
                            title="הוסף בניין חדש"
                        >
                            <Plus size={20} />
                        </button>
                        <BuildingDropdown
                            buildings={availableBuildings}
                            selectedBuildingId={selectedBuildingId}
                            onBuildingChange={handleBuildingChange}
                            isLoading={isBuildingsLoading || isLoadingSubscriptions}
                            hasNoAccess={hasNoAccess}
                            subscriptions={buildingSubscriptions}
                            t={t}
                            bypassSubscriptionCheck={isTrialBypassUser(userData?.email)}
                        />
                    </div>
                )}

                <nav className={styles.sidebarNav}>
                    <ul className={styles.menuList}>
                        {menuItems.map((item) => (
                            <li key={item.id}>
                                <button
                                    className={`${styles.menuItem} ${location.pathname === item.path || (item.hasSubMenu && location.pathname.startsWith('/app/admin-dashboard')) ? styles.menuItemActive : ''
                                        }`}
                                    onClick={() => handleMenuItemClick(item)}
                                    style={{
                                        '--item-color': item.color
                                    }}
                                >
                                    <div className={styles.menuItemIcon}>
                                        {item.icon}
                                    </div>
                                    <span className={styles.menuItemLabel}>{item.label}</span>
                                    {item.hasSubMenu ? (
                                        <ChevronDown 
                                            size={16} 
                                            className={`${styles.subMenuIndicator} ${expandedMenus.has(item.id) ? styles.subMenuIndicatorExpanded : ''}`}
                                        />
                                    ) : (
                                        location.pathname === item.path && (
                                            <ChevronRight size={16} className={styles.activeIndicator} />
                                        )
                                    )}
                                </button>
                                
                                {/* Sub-menu items */}
                                {item.hasSubMenu && expandedMenus.has(item.id) && (
                                    <ul className={styles.subMenu}>
                                        {item.subItems.map((subItem) => (
                                            <li key={subItem.id}>
                                                <button
                                                    className={`${styles.subMenuItem} ${location.pathname + location.search === subItem.path ? styles.subMenuItemActive : ''}`}
                                                    onClick={(e) => handleSubMenuItemClick(subItem.path, e)}
                                                    style={{
                                                        '--item-color': subItem.color
                                                    }}
                                                >
                                                    <div className={styles.subMenuItemIcon}>
                                                        {subItem.icon}
                                                    </div>
                                                    <span className={styles.subMenuItemLabel}>{subItem.label}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className={styles.sidebarFooter}>
                        <button
                            className={styles.logoutButton}
                            onClick={handleLogout}
                        >
                            <LogOut size={20} />
                            <span>{t('logout')}</span>
                        </button>
                    </div>
                </nav>
            </aside>

            {/* Main Content */}
            <div className={styles.mainLayout}>
                {/* Header */}
                <header className={styles.header}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerLeft}>
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className={styles.menuToggle}
                            >
                                <Menu size={22} />
                            </button>
                            <div className={styles.pageTitle}>
                                <h2>{getCurrentPageTitle()}</h2>
                            </div>
                        </div>

                        {/* Desktop Header Center Content - Hide on mobile */}
                        {!isMobile && (
                            <div className={styles.headerCenter}>
                                <button
                                    className={styles.addBuildingButton}
                                    onClick={() => navigate('/app/add-building')}
                                    title="הוסף בניין חדש"
                                >
                                    <Plus size={20} />
                                </button>
                                <BuildingDropdown
                                    buildings={availableBuildings}
                                    selectedBuildingId={selectedBuildingId}
                                    onBuildingChange={handleBuildingChange}
                                    isLoading={isBuildingsLoading || isLoadingSubscriptions}
                                    hasNoAccess={hasNoAccess}
                                    subscriptions={buildingSubscriptions}
                                    t={t}
                                    bypassSubscriptionCheck={isTrialBypassUser(userData?.email)}
                                />
                            </div>
                        )}

                        <div className={styles.headerRight}>
                            {/* Subscription Status - shows when 3 days or less remaining */}
                            <SubscriptionStatusWrapper
                                key={`subscription-${selectedBuildingId}`} // Force remount when building changes
                                selectedBuildingId={selectedBuildingId}
                                buildingSubscriptions={buildingSubscriptions}
                                availableBuildings={availableBuildings}
                                onGoToPayment={(buildingId) => {
                                    // Store building address info for payment form
                                    const selectedBuilding = availableBuildings.find(b => b.buildingID === buildingId);
                                    if (selectedBuilding) {
                                        const buildingNeedPay = {
                                            address: selectedBuilding.address || '',
                                            addressNumber: selectedBuilding.addressNumber || '',
                                            city: selectedBuilding.city || '',
                                            displayAddress: getBuildingDisplayAddress(selectedBuilding)
                                        };
                                        localStorage.setItem('buildingNeedPay', JSON.stringify(buildingNeedPay));
                                    }

                                    navigate('/subscription-payment', {
                                        state: {
                                            buildingId: buildingId,
                                            reason: 'expiring_soon',
                                            returnTo: location.pathname,
                                            buildingName: selectedBuilding ? getBuildingDisplayAddress(selectedBuilding) : 'בניין'
                                        }
                                    });
                                }}
                            />

                            {/* Cross-Building Notification Bell with Real-time Updates */}
                            <NotificationBell
                                hasNewMessages={hasNewMessages}
                                onNotificationClick={handleNotificationClick}
                            />

                            {/* Settings Gear Icon (Admin only) */}
                            {isAdmin() && (
                                <>
                                    <button
                                        className={styles.settingsButton}
                                        onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
                                        title="ניהול מערכת"
                                    >
                                        <Settings size={20} />
                                    </button>

                                    {isSettingsDropdownOpen && createPortal(
                                        <div className={styles.settingsModalOverlay} onClick={() => setIsSettingsDropdownOpen(false)}>
                                            <div className={styles.settingsModal} onClick={(e) => e.stopPropagation()}>
                                                <div className={styles.settingsModalHeader}>
                                                    <h3>ניהול מערכת</h3>
                                                    <button
                                                        className={styles.settingsCloseButton}
                                                        onClick={() => setIsSettingsDropdownOpen(false)}
                                                        aria-label="סגור"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                                <div className={styles.settingsModalContent}>
                                                    {settingsMenuItems.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            className={`${styles.settingsMenuItem} ${selectedSettingsTab === item.id ? styles.settingsMenuItemActive : ''}`}
                                                            onClick={() => handleSettingsMenuClick(item)}
                                                            style={{ '--item-color': item.color }}
                                                        >
                                                            <div className={styles.settingsMenuItemIcon}>
                                                                {item.icon}
                                                            </div>
                                                            <span className={styles.settingsMenuItemLabel}>{item.label}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </>
                            )}

                            {userData && (
                                <div
                                    className={styles.userProfile}
                                    onClick={handleUserProfileClick}
                                >
                                    <div className={styles.userAvatar}>
                                        {(() => {
                                            if (isLoadingProfilePic) {
                                                return <div className={styles.avatarSpinner}>⏳</div>;
                                            } else if (userProfilePicture) {
                                                return (
                                                    <img
                                                        src={userProfilePicture}
                                                        alt="תמונת פרופיל"
                                                        className={styles.avatarImage}
                                                        onError={(e) => {
                                                            setUserProfilePicture(null);
                                                        }}
                                                        onLoad={() => {
                                                        }}
                                                    />
                                                );
                                            } else {
                                                return <User size={20} />;
                                            }
                                        })()}
                                    </div>
                                    <div className={styles.userInfo}>
                                        <span className={styles.userName}>
                                            {userData.name || t('user')}
                                        </span>
                                        <span className={styles.userRole}>
                                            {hasNoAccess ? t('tenant') : t('buildingManager')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className={styles.mainContent}>
                    <div className={styles.contentWrapper}>
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Layout;